import { useQuery } from "@tanstack/react-query";
import { fetchIdentity, type Identity } from "./identityClient";
import { identityQueryKey } from "./queryKeys";
import { useAuth } from "../auth/AuthContext";

export type IdentityStatus = "idle" | "loading" | "ready" | "error";

export interface UseIdentityResult {
    status: IdentityStatus;
    identity: Identity | null;
    error: Error | null;
    retry: () => void;
}

/**
 * Reads the current identity from React Query's cache.
 *
 * PRD-003 invariants encoded as React Query options:
 * - staleTime: Infinity   → never auto-refetched on mount, navigation, or
 *                           focus. The cache is invalidated only by logout
 *                           or by a new JWT changing the query key.
 * - gcTime: Infinity      → cached as long as the query is observed.
 * - retry: false          → a single fetch attempt; failures surface to
 *                           the gate's error state for explicit user retry.
 *
 * The static-analysis script `tools/identity-cache-once-per-jwt.sh` keeps
 * these invariants from regressing silently.
 */
export function useIdentity(): UseIdentityResult {
    const { jwt } = useAuth();
    const query = useQuery({
        queryKey: identityQueryKey(jwt),
        queryFn: fetchIdentity,
        enabled: jwt !== null,
        staleTime: Infinity,
        gcTime: Infinity,
        retry: false,
    });

    let status: IdentityStatus;
    if (jwt === null) {
        status = "idle";
    } else if (query.isError) {
        status = "error";
    } else if (query.data) {
        status = "ready";
    } else {
        status = "loading";
    }

    return {
        status,
        identity: query.data ?? null,
        error: query.error,
        retry: () => {
            void query.refetch();
        },
    };
}
